/**
 * 🚀 SPACE CUSTOMERS - Module Équipage (Clients)
 * Gestion des clients style vaisseau spatial
 * Avec tamponnage des cartes de fidélité directement depuis l'admin !
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Phone, Mail, MapPin, Award, Heart, Trophy,
  MoreVertical, Edit, Eye, Ban, Gift, ChevronRight,
  Loader2, X, CheckCircle, AlertTriangle, CreditCard, Stamp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { toast } from 'sonner';
import DynamicLoyaltyCard from '../DynamicLoyaltyCard';

// Carte client holographique
const CustomerCard = ({ customer, onSelect, onEditPhone, onStamp, onCredit }) => {
  const [expanded, setExpanded] = useState(false);
  const [debtInfo, setDebtInfo] = useState(null);
  const [loadingDebt, setLoadingDebt] = useState(false);

  // Vérifier si le client a une dette dès l'affichage
  useEffect(() => {
    const checkDebt = async () => {
      if (!customer.phone) return;
      setLoadingDebt(true);
      try {
        const result = await api.checkCustomerDebtByPhone?.(customer.phone);
        if (result?.hasDebt) {
          setDebtInfo(result);
        }
      } catch (e) {
        // Pas grave
      } finally {
        setLoadingDebt(false);
      }
    };
    checkDebt();
  }, [customer.phone]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="backdrop-blur-xl rounded-2xl border border-cyan-500/20 bg-[#12121a]/80 overflow-hidden"
    >
      <div 
        onClick={() => setExpanded(!expanded)}
        className="p-4 cursor-pointer hover:bg-cyan-500/5 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar - Photo du client ou initiale */}
            {(customer.avatar || customer.profilePhoto) ? (
              <img 
                src={customer.avatar || customer.profilePhoto} 
                alt={customer.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500/50"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xl font-bold text-white">
                {customer.name?.charAt(0) || '?'}
              </div>
            )}
            
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white text-lg">{customer.name}</h3>
                {debtInfo?.hasDebt && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
                    💳 {debtInfo.balance?.toFixed(2)}€
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {customer.phone}
                </span>
                {customer.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {customer.email.substring(0, 15)}...
                  </span>
                )}
                {customer.completedCardsCount > 0 && (
                  <span className="flex items-center gap-1 text-yellow-400">
                    <Award className="w-3 h-3" />
                    {customer.completedCardsCount} carte{customer.completedCardsCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-cyan-500/10"
          >
            <div className="p-4 space-y-4">
              {/* Infos détaillées complètes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {customer.email && (
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 p-2 rounded-lg">
                    <Mail className="w-4 h-4 text-cyan-400" />
                    <span>{customer.email}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 p-2 rounded-lg">
                    <MapPin className="w-4 h-4 text-green-400" />
                    <span className="truncate">{customer.address}</span>
                  </div>
                )}
                {customer.birthDate && (
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 p-2 rounded-lg">
                    <Gift className="w-4 h-4 text-pink-400" />
                    <span>Né(e) le {new Date(customer.birthDate).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
                {customer.socialNetwork && (
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 p-2 rounded-lg">
                    <Heart className="w-4 h-4 text-red-400" />
                    <span>{customer.socialNetwork}</span>
                  </div>
                )}
                {customer.referralCode && (
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 p-2 rounded-lg">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    <span>Code parrain: <span className="text-yellow-400 font-mono">{customer.referralCode}</span></span>
                  </div>
                )}
                {customer.gamePoints > 0 && (
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 p-2 rounded-lg">
                    <Award className="w-4 h-4 text-purple-400" />
                    <span>{customer.gamePoints} points jeu</span>
                  </div>
                )}
              </div>

              {/* Cartes de fidélité - Aperçu */}
              {customer.cards && (
                <div className="flex gap-2 flex-wrap">
                  {['alcool', 'cbd', 'puffs'].map((type) => {
                    const card = customer.cards?.[type];
                    const stamps = card?.stamps || 0;
                    const max = type === 'puffs' ? 40 : 10;
                    return (
                      <div 
                        key={type}
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: stamps > 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                          color: stamps > 0 ? '#22c55e' : '#9ca3af'
                        }}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}: {stamps}/{max}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onStamp(customer)}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700"
                >
                  <Stamp className="w-4 h-4 mr-1" /> Tamponner
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEditPhone(customer)}
                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                >
                  <Edit className="w-4 h-4 mr-1" /> Modifier
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onCredit(customer, debtInfo)}
                  className={`border-orange-500/30 hover:bg-orange-500/10 ${
                    debtInfo?.hasDebt ? 'text-red-400 border-red-500/30' : 'text-orange-400'
                  }`}
                >
                  <CreditCard className="w-4 h-4 mr-1" /> 
                  {debtInfo?.hasDebt ? `${debtInfo.balance?.toFixed(2)}€` : 'Crédit'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Modal modification téléphone
const EditPhoneModal = ({ customer, isOpen, onClose, onSave }) => {
  const [newPhone, setNewPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPhone.trim()) return;
    
    setIsLoading(true);
    try {
      await api.updateCustomerPhone(customer.id, newPhone);
      toast.success('Téléphone mis à jour !');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#12121a] border-cyan-500/30">
        <DialogHeader>
          <DialogTitle className="text-white">Modifier le téléphone</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400">Ancien numéro</label>
            <Input 
              value={customer?.phone || ''} 
              disabled 
              className="bg-gray-800 border-gray-700 text-gray-400"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400">Nouveau numéro</label>
            <Input 
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="06..."
              className="bg-[#1a1a25] border-cyan-500/30 text-white"
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || !newPhone.trim()}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Modal tamponnage cartes - Version avec templates dynamiques
const StampModal = ({ customer, isOpen, onClose, onSave }) => {
  const [customerData, setCustomerData] = useState(null);
  const [customerCards, setCustomerCards] = useState([]); // Les vraies cartes avec tampons
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fonction de chargement des données
  const loadData = async () => {
    if (!customer?.id) return;
    setIsLoading(true);
    try {
      const [customerResponse, cardsResponse, templatesResponse] = await Promise.all([
        api.getCustomerById(customer.id),
        api.getCustomerCards(customer.id), // Charger les vraies cartes avec tampons !
        api.getCardTemplates(false) // Uniquement les templates actifs
      ]);
      setCustomerData(customerResponse);
      setCustomerCards(cardsResponse || []);
      setTemplates(templatesResponse.templates || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les données complètes du client ET les templates
  useEffect(() => {
    if (isOpen && customer?.id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customer?.id]);

  const handleStampChange = async () => {
    // Recharger les données après un tampon
    await loadData();
    onSave?.();
  };

  if (!customer) return null;

  // Créer les objets carte à partir des VRAIES données de cartes (pas du customer)
  const getCardData = (template) => {
    // Mapper les IDs de template vers les types de carte
    const templateToCardType = {
      'tpl_alcohol': 'alcohol',
      'tpl_cbd': 'cbd',
      'tpl_puffs': 'puffs'
    };
    
    const cardType = templateToCardType[template.id] || template.id;
    
    // Trouver la vraie carte dans customerCards
    const realCard = customerCards.find(c => c.type === cardType);
    
    if (realCard) {
      // Utiliser les données réelles de la carte
      return {
        id: realCard.id,
        type: realCard.type,
        boxes: realCard.boxes || [],
        stamps: realCard.boxes?.filter(b => b).length || 0,
        readyToRedeem: realCard.readyToRedeem || false,
        completedCount: customerData?.completedCardsCount || 0
      };
    }
    
    // Si pas de carte trouvée, retourner une carte vide
    const totalBoxes = template.boxCount || 10;
    return {
      type: cardType,
      boxes: Array(totalBoxes).fill(false),
      stamps: 0,
      completedCount: 0
    };
  };

  // Séparer les cartes par défaut et personnalisées
  const defaultTemplates = templates.filter(t => t.isDefault);
  const customTemplates = templates.filter(t => !t.isDefault);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a12] border-cyan-500/30 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-3">
            <Stamp className="w-6 h-6 text-green-400" />
            Tamponner - {customer.name}
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : customerData ? (
          <div className="space-y-6 py-4">
            {/* Statistiques du client */}
            {customerData.completedCardsCount > 0 && (
              <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="text-green-400 font-semibold">
                      {customerData.completedCardsCount} carte{customerData.completedCardsCount > 1 ? 's' : ''} complétée{customerData.completedCardsCount > 1 ? 's' : ''}
                    </div>
                    <div className="text-gray-400 text-sm">Historique des récompenses obtenues</div>
                  </div>
                </div>
                <Trophy className="w-8 h-8 text-yellow-500/50" />
              </div>
            )}

            {/* Cartes par défaut */}
            {defaultTemplates.length > 0 && (
              <>
                <h3 className="text-gray-400 text-sm font-medium">Cartes principales</h3>
                {defaultTemplates.map((template) => (
                  <DynamicLoyaltyCard
                    key={template.id}
                    template={template}
                    card={getCardData(template)}
                    customer={customerData}
                    onStampChange={handleStampChange}
                  />
                ))}
              </>
            )}
            
            {/* Cartes personnalisées */}
            {customTemplates.length > 0 && (
              <>
                <h3 className="text-gray-400 text-sm font-medium mt-8 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Cartes personnalisées
                </h3>
                {customTemplates.map((template) => (
                  <DynamicLoyaltyCard
                    key={template.id}
                    template={template}
                    card={getCardData(template)}
                    customer={customerData}
                    onStampChange={handleStampChange}
                  />
                ))}
              </>
            )}
            
            {templates.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune carte de fidélité configurée</p>
                <p className="text-sm mt-1">Créez des cartes dans le module &quot;Cartes Fidélité&quot;</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            Impossible de charger les données du client
          </div>
        )}
        
        <div className="flex justify-end pt-4 border-t border-gray-800">
          <Button onClick={onClose} variant="outline" className="border-gray-600">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Module Clients principal
const SpaceCustomers = ({ onSelectCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [stampingCustomer, setStampingCustomer] = useState(null);
  const [creditCustomer, setCreditCustomer] = useState(null);
  const [creditDebtInfo, setCreditDebtInfo] = useState(null);

  // Charger les clients
  const loadCustomers = async (search = '') => {
    setIsLoading(true);
    try {
      const data = await api.searchAllCustomers(search);
      setCustomers(data || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const handleSearch = () => {
    loadCustomers(searchQuery);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-500">
            👥 ÉQUIPAGE
          </h1>
          <p className="text-gray-500 text-sm">{customers.length} membres inscrits</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Rechercher par nom, téléphone..."
            className="pl-10 bg-[#1a1a25] border-cyan-500/30 text-white"
          />
        </div>
        <Button
          onClick={handleSearch}
          className="bg-gradient-to-r from-cyan-500 to-blue-600"
        >
          <Search className="w-4 h-4 mr-2" />
          Rechercher
        </Button>
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Aucun client trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {customers.map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onSelect={onSelectCustomer}
              onEditPhone={() => setEditingCustomer(customer)}
              onStamp={() => setStampingCustomer(customer)}
              onCredit={(cust, debtInfo) => {
                setCreditCustomer(cust);
                setCreditDebtInfo(debtInfo);
              }}
            />
          ))}
        </div>
      )}

      {/* Modal édition téléphone */}
      <EditPhoneModal
        customer={editingCustomer}
        isOpen={!!editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSave={() => loadCustomers(searchQuery)}
      />

      {/* Modal tamponnage */}
      <StampModal
        customer={stampingCustomer}
        isOpen={!!stampingCustomer}
        onClose={() => setStampingCustomer(null)}
        onSave={() => loadCustomers(searchQuery)}
      />

      {/* Modal crédit */}
      <CreditModal
        customer={creditCustomer}
        debtInfo={creditDebtInfo}
        isOpen={!!creditCustomer}
        onClose={() => {
          setCreditCustomer(null);
          setCreditDebtInfo(null);
        }}
        onSave={() => loadCustomers(searchQuery)}
      />
    </div>
  );
};

// Modal de gestion des crédits
const CreditModal = ({ customer, debtInfo, isOpen, onClose, onSave }) => {
  const [creditClientData, setCreditClientData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionType, setActionType] = useState(null); // 'add_credit' | 'add_payment'
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger les données du client crédit
  useEffect(() => {
    const loadCreditData = async () => {
      if (!isOpen || !customer) return;
      
      setIsLoading(true);
      try {
        // Si on a déjà un creditClientId, charger directement
        if (debtInfo?.creditClientId) {
          const data = await api.getCreditClient?.(debtInfo.creditClientId);
          setCreditClientData(data);
        } else {
          // Chercher par téléphone - utiliser la nouvelle route qui trouve même sans dette
          const findResult = await api.findCreditClientByPhone?.(customer.phone);
          if (findResult?.found && findResult?.client) {
            setCreditClientData(findResult.client);
          } else {
            setCreditClientData(null);
          }
        }
      } catch (error) {
        console.error('Erreur chargement crédit:', error);
        setCreditClientData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadCreditData();
  }, [isOpen, customer, debtInfo]);

  // Créer un nouveau client crédit
  const handleCreateCreditClient = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    setIsSubmitting(true);
    try {
      const newClient = await api.createCreditClient?.({
        name: customer.name,
        phone: customer.phone,
        initialCredit: parseFloat(amount),
        notes: notes || `Créé depuis fiche client ${customer.name}`
      });
      
      toast.success(`✅ Crédit de ${amount}€ accordé à ${customer.name}`);
      setCreditClientData(newClient);
      setActionType(null);
      setAmount('');
      setNotes('');
      onSave?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ajouter un crédit
  const handleAddCredit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.addCreditTransaction?.(creditClientData.id, {
        amount: parseFloat(amount),
        type: 'credit',
        notes: notes || 'Crédit ajouté'
      });
      
      toast.success(`✅ +${amount}€ ajouté au crédit`);
      
      // Recharger les données
      const data = await api.getCreditClient?.(creditClientData.id);
      setCreditClientData(data);
      setActionType(null);
      setAmount('');
      setNotes('');
      onSave?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ajouter un paiement (remboursement)
  const handleAddPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.addCreditTransaction?.(creditClientData.id, {
        amount: parseFloat(amount),
        type: 'payment',
        notes: notes || 'Remboursement'
      });
      
      toast.success(`✅ Paiement de ${amount}€ enregistré`);
      
      // Recharger les données
      const data = await api.getCreditClient?.(creditClientData.id);
      setCreditClientData(data);
      setActionType(null);
      setAmount('');
      setNotes('');
      onSave?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a12] border-orange-500/30 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-orange-400" />
            Crédit - {customer?.name}
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
          </div>
        ) : creditClientData ? (
          <div className="space-y-4 py-4">
            {/* Solde actuel */}
            <div className={`p-4 rounded-xl ${
              creditClientData.currentBalance > 0 
                ? 'bg-red-500/10 border border-red-500/30' 
                : 'bg-green-500/10 border border-green-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Solde actuel</p>
                  <p className={`text-3xl font-bold ${
                    creditClientData.currentBalance > 0 ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {creditClientData.currentBalance?.toFixed(2)}€
                  </p>
                </div>
                {creditClientData.currentBalance > 100 && (
                  <div className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-bold">
                    ⚠️ Alerte
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            {!actionType ? (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setActionType('add_credit')}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Ajouter crédit
                </Button>
                <Button
                  onClick={() => setActionType('add_payment')}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Enregistrer paiement
                </Button>
              </div>
            ) : (
              <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
                <h4 className="text-white font-semibold">
                  {actionType === 'add_credit' ? '➕ Ajouter au crédit' : '💰 Enregistrer un paiement'}
                </h4>
                <div>
                  <label className="text-sm text-gray-400">Montant (€)</label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="bg-black/50 border-gray-700 text-white text-lg"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">Notes (optionnel)</label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ex: Achat du 10/01..."
                    className="bg-black/50 border-gray-700 text-white"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setActionType(null);
                      setAmount('');
                      setNotes('');
                    }}
                    variant="outline"
                    className="flex-1 border-gray-600"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={actionType === 'add_credit' ? handleAddCredit : handleAddPayment}
                    disabled={isSubmitting || !amount}
                    className={`flex-1 ${
                      actionType === 'add_credit' 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                  </Button>
                </div>
              </div>
            )}

            {/* Historique */}
            {creditClientData.transactions?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-gray-400 text-sm font-medium">Historique récent</h4>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {creditClientData.transactions?.slice(0, 10).map((tx, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-2 rounded-lg bg-white/5 text-sm"
                    >
                      <div>
                        <p className="text-white">{tx.notes || (tx.type === 'credit' ? 'Crédit' : 'Paiement')}</p>
                        <p className="text-gray-500 text-xs">
                          {new Date(tx.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <p className={`font-bold ${tx.type === 'credit' ? 'text-red-400' : 'text-green-400'}`}>
                        {tx.type === 'credit' ? '+' : '-'}{tx.amount?.toFixed(2)}€
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Pas de compte crédit */}
            <div className="text-center py-6">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400 mb-4">Ce client n&apos;a pas encore de compte crédit</p>
              
              {actionType === 'create' ? (
                <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10 text-left">
                  <h4 className="text-white font-semibold">🆕 Créer un compte crédit</h4>
                  <div>
                    <label className="text-sm text-gray-400">Montant initial du crédit (€)</label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="bg-black/50 border-gray-700 text-white text-lg"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Notes (optionnel)</label>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Ex: Nouveau client régulier..."
                      className="bg-black/50 border-gray-700 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setActionType(null);
                        setAmount('');
                        setNotes('');
                      }}
                      variant="outline"
                      className="flex-1 border-gray-600"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={handleCreateCreditClient}
                      disabled={isSubmitting || !amount}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-500"
                    >
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer le compte'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => setActionType('create')}
                  className="bg-gradient-to-r from-orange-500 to-red-500"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Ouvrir un compte crédit
                </Button>
              )}
            </div>
          </div>
        )}
        
        <div className="flex justify-end pt-4 border-t border-gray-800">
          <Button onClick={onClose} variant="outline" className="border-gray-600">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpaceCustomers;
